// Применение темы витрины (docs/07-miniapp-ux.md §1.1): цвета — из Telegram.WebApp.themeParams,
// акцент — из настроек магазина. Пишем CSS-переменные напрямую в DOM (не через React), чтобы
// перекраска не ждала лишнего рендера и работала одинаково для всех компонентов ниже по дереву.
import type { components } from "@shopkit/shared";

import type { ColorScheme, ThemeParams } from "./telegram";

export type ShopTheme = components["schemas"]["ShopTheme"];

interface Palette {
  bg: string;
  secondaryBg: string;
  text: string;
  hint: string;
  link: string;
  button: string;
  buttonText: string;
}

// Переключатель темы в настройках магазина (light/dark) не может опираться на
// Telegram.WebApp.themeParams: тот всегда отражает реальную тему клиента. Поэтому
// для принудительного light/dark берём собственные фолбэк-палитры, а не то, что прислал Telegram.
const LIGHT_PALETTE: Palette = {
  bg: "#ffffff",
  secondaryBg: "#f0f0f0",
  text: "#000000",
  hint: "#999999",
  link: "#2481cc",
  button: "#2481cc",
  buttonText: "#ffffff",
};

const DARK_PALETTE: Palette = {
  bg: "#1c1c1e",
  secondaryBg: "#2c2c2e",
  text: "#ffffff",
  hint: "#8e8e93",
  link: "#5ac8fa",
  button: "#5ac8fa",
  buttonText: "#000000",
};

// Небольшая именованная палитра акцентов продавца. api/openapi.yaml не задаёт перечень
// допустимых значений accent (свободная строка) — набор ключей ниже согласован с тем,
// что предлагает выбрать админка на экране настроек темы (вне doc-скоупа этой задачи).
const ACCENT_PALETTE: Record<string, string> = {
  blue: "#2481cc",
  purple: "#7c3aed",
  green: "#0e9f6e",
  orange: "#f59e0b",
  red: "#ef4444",
  pink: "#ec4899",
};

const CORNER_RADIUS: Record<ShopTheme["corners"], string> = {
  rounded: "16px",
  sharp: "4px",
};

function paletteFor(mode: ShopTheme["mode"], colorScheme: ColorScheme, themeParams: ThemeParams): Palette {
  if (mode === "light") return LIGHT_PALETTE;
  if (mode === "dark") return DARK_PALETTE;
  const base = colorScheme === "dark" ? DARK_PALETTE : LIGHT_PALETTE;
  return {
    bg: themeParams.bg_color ?? base.bg,
    secondaryBg: themeParams.secondary_bg_color ?? base.secondaryBg,
    text: themeParams.text_color ?? base.text,
    hint: themeParams.hint_color ?? base.hint,
    link: themeParams.link_color ?? base.link,
    button: themeParams.button_color ?? base.button,
    buttonText: themeParams.button_text_color ?? base.buttonText,
  };
}

function resolveAccent(accent: string, fallbackButtonColor: string): string {
  return ACCENT_PALETTE[accent] ?? fallbackButtonColor;
}

export interface ApplyThemeInput {
  themeParams: ThemeParams;
  colorScheme: ColorScheme;
  shopTheme: ShopTheme;
  /** Элемент, на который пишутся CSS-переменные. По умолчанию — document.documentElement. */
  target?: HTMLElement;
}

/** applyTheme выставляет CSS-переменные темы витрины на переданный (или корневой) элемент. */
export function applyTheme({ themeParams, colorScheme, shopTheme, target }: ApplyThemeInput): void {
  const element = target ?? document.documentElement;
  const palette = paletteFor(shopTheme.mode, colorScheme, themeParams);

  element.style.setProperty("--tg-bg-color", palette.bg);
  element.style.setProperty("--tg-secondary-bg-color", palette.secondaryBg);
  element.style.setProperty("--tg-text-color", palette.text);
  element.style.setProperty("--tg-hint-color", palette.hint);
  element.style.setProperty("--tg-link-color", palette.link);
  element.style.setProperty("--tg-button-color", palette.button);
  element.style.setProperty("--tg-button-text-color", palette.buttonText);
  element.style.setProperty("--shop-accent-color", resolveAccent(shopTheme.accent, palette.button));
  element.style.setProperty("--shop-corner-radius", CORNER_RADIUS[shopTheme.corners]);
}
